"use client";

import React from "react";
import dynamic from "next/dynamic";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getProductValue } from "@/lib/utils/product";
import { isProductArEnabled, parseArLink } from "@/lib/utils/ar";

const ProductModelViewer = dynamic(() => import("./ProductModelViewer"), {
  ssr: false,
  loading: () => (
    <div className="flex h-[300px] w-full items-center justify-center glass-box lg:h-[375px]">
      <span className="text-xs text-mute">در حال بارگذاری مدل سه‌بعدی...</span>
    </div>
  ),
});

const SpecRow = ({ label, value, children }) => (
  <div className="flex flex-wrap items-baseline ">
    <span className="text-mute whitespace-nowrap ">{label} : </span>
    <div className={`pr-1 ${children ? "flex-1" : "font-semibold"}`}>
      {value || children}
    </div>
  </div>
);

const BadgeList = ({ items }) => (
  <span className="inline-flex flex-wrap text-sm lg:text-base">
    {items.map((item, index) => (
      <span key={index} className="px-1.5 border-l last:border-l-0">
        {getProductValue(item)}
      </span>
    ))}
  </span>
);

export default function SingleProductDetail({ product = {} }) {
  const dimensions = product.width && product.height && product.length
    ? `${product.width} * ${product.height} * ${product.length} سانتی‌متر`
    : null;
  const weight = product.weight || null;
  const material = getProductValue(product.material) || null;
  const environment = Array.isArray(product.environment)
    ? product.environment
    : product.environment
    ? [product.environment]
    : [];
  const tags = Array.isArray(product.tags) ? product.tags : [];
  const content = getProductValue(product.content) || "";
  const hasArModel =
    isProductArEnabled(product?.ar) && !!parseArLink(product?.ar_link).glb;

  return (
    <section className="flex w-full flex-col gap-4 text-sm lg:flex-row lg:text-lg">
      <Tabs defaultValue="specs" className="order-1 w-full lg:min-w-0 lg:flex-1">
          <TabsList className="mb-4">
            <TabsTrigger className="px-4 py-2 rounded-2xl cursor-pointer" value="specs">
             <h2 className="!text-base md:!text-lg "> مشخصات محصول</h2>
            </TabsTrigger>
            <TabsTrigger className="px-4 py-2 rounded-2xl cursor-pointer" value="desc">
             <h2 className="!text-base md:!text-lg "> توضیحات محصول</h2>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="specs" className={""}>
            <div className="w-full glass-box p-4 md:p-8 space-y-4  lg:space-y-5 ">
              <h3 className=" lg:font-bold !text-base md:!text-xl !m-0 pb-4">مشخصات کلی</h3>
              
              {dimensions && <SpecRow label="ابعاد محصول" value={dimensions} />}
              {weight && <SpecRow label="وزن" value={`${weight} کیلوگرم`} />}
              {material && <SpecRow label="جنس" value={material} />}

              {(environment.length > 0 || tags.length > 0) && (
                <>
                  <h3 className=" lg:font-bold !text-base md:!text-xl  pb-4 !m-0">جزئیات بیشتر</h3>

                  {environment.length > 0 && (
                    <SpecRow label="مناسب برای محیط">
                      <BadgeList items={environment} />
                    </SpecRow>
                  )}

                  {tags.length > 0 && (
                    <SpecRow label="برچسب‌ها">
                      <BadgeList items={tags} />
                    </SpecRow>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="desc">
            <div className="w-full glass-box p-8">
              <h2 className="font-bold text-2xl mb-4">توضیحات محصول</h2>
              {content ? (
                <div
                  className="text-mute text-base leading-relaxed [&_a]:text-primary [&_a]:underline [&_li]:list-disc [&_li]:mr-5 [&_p]:mb-3"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              ) : (
                <p className="text-mute text-base leading-relaxed">
                  توضیحات محصول در دسترس نیست.
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>

      {hasArModel && (
        <div className="order-2 w-full shrink-0 lg:w-[400px]">
          <div className="lg:sticky lg:top-10 lg:z-10">
            <ProductModelViewer product={product} />
          </div>
        </div>
      )}
    </section>
  );
}
